import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { TAGS_KEYS } from "@/other/constants/TagsKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useState, useEffect, useMemo } from "react";
import { TagValue } from "@/bryntum-lib/models/TagValue";
import { toast } from "sonner";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

const TaskPhaseDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user } = useSwivuBryntumContext();
  const [phases, setPhases] = useState<TagValue[]>([]);

  const canEdit = useMemo<boolean>(() => {
    if (!user) return false;
    return user.can(GRANTS.grant_one);
  }, [user])

  const { subscribe } = useSwivuStore({
    storeId: STORE_KEYS.taskTagValuesStore,
  })

  const loadPhases = () => {
    if (!project) return;
    const tagValueStore = project.taskTagValues;
    const phaseRecords = tagValueStore.query((record: TagValue) => record.tagSet === TAGS_KEYS.phase, true) || [];
    setPhases(() => phaseRecords);
  }

  useEffect(() => {
    loadPhases();
    return subscribe(() => {
      loadPhases();
    })
  }, [subscribe]);

  const isDuplicatePhaseName = (name: string, excludePhaseId?: string | number) => {
    if (!project) return false;
    const tagValueStore = project.taskTagValues;
    const allPhases = tagValueStore.query((record: TagValue) => record.tagSet === TAGS_KEYS.phase, true) || [];
    return allPhases.some(p => {
      if (excludePhaseId !== undefined && p.id === excludePhaseId) {
        return false;
      }
      return p.tagValue.toLowerCase() === name.toLowerCase();
    });
  };

  const handleAddItem = (value: string) => {
    if (!value.trim()) {
      return;
    }

    if (isDuplicatePhaseName(value)) {
      toast.error('Phase name already exists');
      return;
    }

    if (!project) return;
    const tagValueStore = project.taskTagValues;
    const newPhase = tagValueStore.createRecord({
      tagSet: TAGS_KEYS.phase,
      tagValue: value,
    });
    tagValueStore.add(newPhase);
  }

  const handleReorderItems = (movedItem: TagValue, beforeItem: TagValue | null) => {
    if (!project) return;
    const tagValueStore = project.taskTagValues;
    // @ts-expect-error - tagValue is a TagValue model
    tagValueStore.move(movedItem, beforeItem);
  }

  const handleUpdatePhaseName = (phase: TagValue, newName: string) => {
    if (!newName.trim()) {
      return false;
    }

    if (isDuplicatePhaseName(newName, phase.id)) {
      toast.error('Phase name already exists');
      return false;
    }

    phase.tagValue = newName;
    return true;
  }

  const handleDeletePhase = (phase: TagValue) => {
    if (!project) return;
    const tagValueStore = project.taskTagValues;
    // @ts-expect-error - phase is a TagValue model
    tagValueStore.remove(phase);
  }

  return (
    <div className="p-6 flex flex-col max-w-3xl w-full h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <SwivuList.Root<TagValue>
        items={phases}
        onReorder={handleReorderItems}
        searchConfig={{
          fields: ["tagValue"],
          placeholder: "Search phases...",
        }}
      >
        <SwivuList.Toolbar />

        <SwivuList.Content<TagValue> emptyMessage="No phases yet. Create your first phase!">
          {(phase: TagValue) => (
            <SwivuList.Item value={phase.id as string | number}>
              {/* Drag Handle */}
              <SwivuList.ItemHandle disabled={!canEdit} value={phase.id as string | number} />

              {/* Phase Name */}
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <SwivuList.TextField
                  item={phase}
                  value={phase.tagValue}
                  onUpdate={handleUpdatePhaseName}
                  placeholder="Enter phase name"
                  disabled={!canEdit}
                  className="flex-1"
                />
              </div>

              {/* Actions */}
              <SwivuList.ItemActions
                disabled={!canEdit}
                onDelete={() => handleDeletePhase(phase)}
              />
            </SwivuList.Item>
          )}
        </SwivuList.Content>

        <SwivuList.QuickAdd
          disabled={!canEdit}
          onAdd={handleAddItem}
          placeholder="Add new phase..."
        />
      </SwivuList.Root>
    </div>
  );
};

export default TaskPhaseDetail;
